import { useState } from "react";
import { Button, FormControlLabel, Switch, Tab, Tabs, Tooltip } from "@mui/material";

import { ModalTemplate } from "../../../components/modal-template/modal-template.comp";
import {
    activateIntegration,
    deactivateIntegration,
    updateIntegration
} from "../../../api/admin/integrations/endpoints-referrers";
import type {
    Integration,
    UpdateIntegrationRequest
} from "../../../common/interfaces/admin/integration.interface";
import IntegrationBasicsStep from "./components/integration-basics-step.comp";
import IntegrationBehaviorStep from "./components/integration-behavior-step.comp";
import IntegrationBrandingStep from "./components/integration-branding-step.comp";
import IntegrationSsoConfig from "./components/integration-sso-config.comp";
import IntegrationSignedIdentityConfig from "./components/integration-signed-identity-config.comp";
import type { KnowledgeBaseOption } from "./components/integration-basics-step.comp";
import SnippetPreview from "./components/snippet-preview.comp";
import LinkUrlPreview from "./components/link-url-preview.comp";

interface Props {
    open: boolean;
    companyId: number;
    integration: Integration;
    knowledgeBases: KnowledgeBaseOption[];
    /** Tab to open on first render (e.g. "sso" right after creating an SSO row). */
    initialTabKey?: string;
    onClose: () => void;
    /** Called with the refreshed integration after any successful mutation. */
    onUpdated: (updated: Integration) => void;
    onDeleteRequest: () => void;
}

const IntegrationEditModal = ({
    open,
    companyId,
    integration,
    knowledgeBases,
    initialTabKey,
    onClose,
    onUpdated,
    onDeleteRequest
}: Props) => {
    // authMode is fixed at creation, so the tab set is stable for a given row.
    const ssoEnabled = integration.authMode === "sso_redirect";
    const signedEnabled = integration.authMode === "signed_identity";
    const tabKeys: string[] = [
        "basics",
        "behavior",
        "branding",
        ...(ssoEnabled ? ["sso"] : []),
        ...(signedEnabled ? ["signed"] : []),
        "snippet"
    ];

    const [draft, setDraft] = useState<UpdateIntegrationRequest>({});
    const [tab, setTab] = useState(() => {
        const i = initialTabKey ? tabKeys.indexOf(initialTabKey) : -1;
        return i >= 0 ? i : 0;
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const current = { ...integration, ...draft };
    const patch = (next: Partial<UpdateIntegrationRequest>) =>
        setDraft(prev => ({ ...prev, ...next }));
    const dirty = Object.keys(draft).length > 0;

    const TAB_LABELS: Record<string, string> = {
        basics: "Basics",
        behavior: "Behavior",
        branding: "Branding",
        sso: "SSO",
        signed: "Signed Identity",
        snippet: "Embed snippet"
    };
    const activeKey = tabKeys[Math.min(tab, tabKeys.length - 1)] ?? "basics";
    const editableTab =
        activeKey === "basics" || activeKey === "behavior" || activeKey === "branding";

    // FR-11 surfaced client-side: an sso_redirect integration can't be activated
    // until it has a passing test; signed_identity needs a stored config first.
    // The backend enforces this too — disabling the toggle just avoids a
    // guaranteed-failing request.
    const activationBlocked =
        (ssoEnabled && !integration.isActive && integration.lastSsoTestOutcome !== "passed") ||
        (signedEnabled && !integration.isActive && !integration.signedIdentityConfigured);

    const activationHint = !activationBlocked
        ? ""
        : ssoEnabled
          ? "A passing SSO test is required before activating."
          : "Configure the signed-identity hand-off before activating.";

    const save = async () => {
        if (!dirty) return;
        setSaving(true);
        setError(null);
        try {
            const { data } = await updateIntegration(companyId, integration.id, draft);
            onUpdated(data);
            setDraft({});
        } catch (err: any) {
            setError(err?.response?.data?.message ?? "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async () => {
        setError(null);
        try {
            const action = integration.isActive ? deactivateIntegration : activateIntegration;
            const { data } = await action(companyId, integration.id);
            onUpdated(data);
        } catch (err: any) {
            setError(err?.response?.data?.message ?? "Activate failed");
        }
    };

    return (
        <ModalTemplate open={open} width={720} handleClose={onClose} ariaLabel="Edit integration">
            <div className="embed-integration-modal">
                <h2 className="embed-integration-modal__title">Edit {integration.name}</h2>

                <div className="embed-integration-modal__active">
                    <Tooltip title={activationHint} disableHoverListener={!activationBlocked}>
                        <span>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={integration.isActive}
                                        onChange={toggleActive}
                                        disabled={activationBlocked}
                                    />
                                }
                                label={integration.isActive ? "Active" : "Inactive"}
                            />
                        </span>
                    </Tooltip>
                </div>

                <Tabs
                    className="embed-integration-modal__tabs"
                    value={Math.min(tab, tabKeys.length - 1)}
                    onChange={(_, v) => setTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {tabKeys.map(key => (
                        <Tab key={key} label={TAB_LABELS[key]} />
                    ))}
                </Tabs>

                <div className="embed-integration-modal__body">
                    {activeKey === "basics" && (
                        <IntegrationBasicsStep
                            value={current}
                            onChange={patch}
                            knowledgeBases={knowledgeBases}
                            knowledgeBaseLocked
                        />
                    )}
                    {activeKey === "behavior" && (
                        <IntegrationBehaviorStep value={current} onChange={patch} />
                    )}
                    {activeKey === "branding" && (
                        <IntegrationBrandingStep value={current} onChange={patch} />
                    )}
                    {activeKey === "sso" && (
                        <IntegrationSsoConfig
                            companyId={companyId}
                            integration={integration}
                            onUpdated={onUpdated}
                        />
                    )}
                    {activeKey === "signed" && (
                        <IntegrationSignedIdentityConfig
                            companyId={companyId}
                            integration={integration}
                            onUpdated={onUpdated}
                        />
                    )}
                    {activeKey === "snippet" && (
                        <div className="embed-integration-modal__snippet">
                            <SnippetPreview
                                integrationId={integration.id}
                                scriptUrl={integration.scriptUrl}
                            />
                            <LinkUrlPreview linkUrl={integration.linkUrl} />
                        </div>
                    )}
                </div>

                {error && <div className="embed-integration-modal__error">{error}</div>}

                <div className="embed-integration-modal__actions">
                    <Button
                        className="embed-integration-modal__delete"
                        variant="text"
                        color="error"
                        onClick={onDeleteRequest}
                    >
                        Delete
                    </Button>
                    <div className="embed-integration-modal__actions-right">
                        <Button
                            className="embed-integration-modal__cancel"
                            variant="text"
                            onClick={onClose}
                        >
                            Close
                        </Button>
                        {editableTab && (
                            <Button
                                className="embed-integration-modal__submit"
                                variant="contained"
                                onClick={save}
                                disabled={!dirty || saving}
                            >
                                {saving ? "Saving…" : "Save changes"}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </ModalTemplate>
    );
};

export default IntegrationEditModal;
